import React, { useEffect, useMemo, useState } from 'react'
import CurrentlyNoItems from '../../components/CurrentlyNoItems'
import _ from 'lodash'
import { DataTable } from 'react-native-paper'

import { useSelector } from 'react-redux'
import {
  FlatList,
  RefreshControl,
  ScrollView,
  View,
  StyleSheet
} from 'react-native'
import spacing from '../../styles/spacing'
import { currentlyNoTemplateDefaults } from '../CategoryViewScreen'
import { flattenEntity } from '../../helpers/entity'
import useBetterLinkTo from '../../useBetterLinkTo'
import useTableSort from '../../hooks/useTableSort'
import MaskInput, { createNumberMask } from 'react-native-mask-input'
import DataTableEntityRow from './DataTableEntityRow'
import colors from '../../styles/colors'
export const cellStyle = {
  minWidth: 150,
  maxWidth: 500,
  borderRightColor: colors.lightestGray,
  paddingLeft: spacing.m1,
  borderRightWidth: StyleSheet.hairlineWidth,
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1
}

export const headerCellStyle = {
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1
}

export const formatTotalCost = (value) => {
  if (value) {
    const dollar = createNumberMask({
      prefix: ['$', ''],
      delimiter: ',',
      separator: '.',
      precision: 2
    })

    return (
      <MaskInput
        value={value}
        mask={dollar}
        editable={false}
        style={{ textAlign: 'center' }}
      />
    )
  } else {
    return null
  }
}

export const sortColumnHeaders = (
  columnHeaders,
  isForCustomReport,
  reportEntity = null
) => {
  if (reportEntity) {
    const groupByField =
      reportEntity?.group_by_params?.groupByField?.field_data?.property
    const aggregateField =
      reportEntity?.group_by_params?.aggregateField?.field_data?.property
    const aggregateFunction =
      reportEntity?.group_by_params?.aggregateFunction?.id

    if (groupByField && aggregateField) {
      const headers = _.chain(columnHeaders ?? [])
        .filter(
          (f) =>
            f.field_data?.property == groupByField ||
            f.field_data?.property == aggregateField
        )
        .sortBy((f) => {
          return _.get(f, 'field_data.table_sort_order', 10000) + '_' + f.id
        })
        .value()

      if (aggregateFunction) {
        headers.push({
          field_data: {
            params: {},
            property: aggregateFunction + '_' + aggregateField,
            title: aggregateFunction
          }
        })
      }

      return headers
    }
  }
  return _.chain(columnHeaders ?? [])
    .sortBy((f) => {
      if (f.field_data?.table_sort_order || isForCustomReport) {
        const key = _.get(f, 'field_data.table_sort_order', 10000) + '_' + f.id
        return key
      } else {
        return _.get(f, 'field_data.sort_order', 999)
      }
    })
    .sortBy((f) => _.get(f, 'field_data.page', 1))
    .value()
}
const DataTableWrapper = ({
  sourceKey,
  shouldSplit,
  entityType,
  reportEntity = null,
  filterToFields,
  refreshing,
  onRefresh,
  loadMoreItems,
  splitviewNavigate,
  isForCustomReport = false,
  forceShowLabelInTable = false,
  canNavigate = true,
  children
}) => {
  const linkTo = useBetterLinkTo()
  const [columnHeaders, updateColumnHeaders] = useState([])

  //TODO: refactor out this common functionality that is also in datalistwrapper.
  const entityListWrapper = useSelector((state) => {
    const entityListWrapper = entityType
      ? state.entities?.[sourceKey] ?? {
          data: null,
          meta: null,
          hasSeenData: false
        }
      : { data: null, meta: null, hasSeenData: false }

    return entityListWrapper
  })

  useEffect(() => {
    if (entityType) {
      const headersToUse = entityType.fields.filter((f) => {
        if (isForCustomReport) {
          return filterToFields
            ? filterToFields.find(
                (filterField) =>
                  filterField.field_data?.property == f.field_data?.property
              )
            : true
        } else {
          return _.get(f, 'field_data.flags.tableView', false)
        }
      })

      updateColumnHeaders(headersToUse)
    }
  }, [entityType, filterToFields, isForCustomReport])

  const { data: entityListItems } = entityListWrapper

  const showLabelInTable =
    _.get(entityType, 'object_data.show_label_table_view', false) ||
    forceShowLabelInTable

  const showAssigneeInTable =
    !!_.get(entityType, 'object_data.show_assignee', false) ||
    entityType?.is_task

  const showApprovalsInTable =
    entityType?.fields?.filter(
      (f) => f.field_data?.params?.approverRoles?.length > 0
    ).length > 0

  const sortedColumnHeaders = useMemo(
    () => sortColumnHeaders(columnHeaders, isForCustomReport, reportEntity),
    [columnHeaders, isForCustomReport, reportEntity]
  )

  const currentlyNoLabel = useMemo(() => {
    return currentlyNoTemplateDefaults({ label: entityType?.label })
  }, [entityType])

  const [sort, activeSort, sortDirection] = useTableSort()

  const resortedEntityListItems = useMemo(() => {
    if (!sort) {
      return entityListItems
    } else {
      if (reportEntity?.group_by_params?.aggregateField) {
        const groupByField =
          reportEntity?.group_by_params?.groupByField?.field_data?.property
        const aggregateField =
          reportEntity?.group_by_params?.aggregateField?.field_data?.property
        const aggregateFunction =
          reportEntity?.group_by_params?.aggregateFunction?.id

        const grouped = _.groupBy(entityListItems, (el) => {
          const flattened = flattenEntity(el, true, reportEntity)
          return flattened[
            reportEntity?.group_by_params?.groupByField?.field_data?.property
          ]
        })

        const aggregated = Object.keys(grouped).map((group) => {
          const groupData = grouped[group]
          let aggregatedValue = 0

          if (aggregateFunction === 'count') {
            aggregatedValue = groupData.length
          } else {
            aggregatedValue = groupData.reduce((acc, curr) => {
              try {
                let value = parseFloat(curr[aggregateField])

                if (!isNaN(value)) {
                  const timeParts = curr[aggregateField].split(':')
                  if (timeParts.length === 3) {
                    const [hours, minutes, seconds] = timeParts.map(parseFloat)
                    value = hours + minutes / 60 + seconds / 3600
                  }
                  return acc + Math.round(value * 100) / 100
                }
                return acc
              } catch (e) {
                console.warn('Error', e)
                return acc
              }
            }, 0)

            if (aggregateFunction === 'average') {
              aggregatedValue =
                Math.round((aggregatedValue / groupData.length) * 100) / 100
            }
          }

          return {
            [groupByField]: groupData[0][groupByField],
            [aggregateFunction + '_' + aggregateField]: aggregatedValue,
            id: groupData[0].id
          }
        })

        return aggregated
      } else {
        let sorted = _.sortBy(entityListItems, (el) => {
          const flattened = flattenEntity(el, true, reportEntity)

          return ('' + flattened[activeSort]).toLowerCase()
        })

        if (sortDirection == 'descending') {
          sorted = sorted.reverse()
        }
        return sorted
      }
    }
  }, [entityListItems, activeSort, sortDirection, reportEntity, sort])

  return (
    <>
      {!resortedEntityListItems.length &&
        (refreshing ? null : <CurrentlyNoItems label={currentlyNoLabel} />)}
      {!!resortedEntityListItems.length && (
        <ScrollView
          horizontal={true}
          scrollEnabled={true}
          contentContainerStyle={{ flexDirection: 'column' }}
        >
          <View style={{ flexDirection: 'column', flex: 1 }}>
            {children}
            <DataTable style={{ flex: 1 }}>
              <DataTable.Header>
                {showLabelInTable && (
                  <DataTable.Title
                    key={'label'}
                    style={cellStyle}
                    sortDirection={activeSort == 'label' ? sortDirection : null}
                    onPress={sort('label')}
                  >
                    Label
                  </DataTable.Title>
                )}
                {showAssigneeInTable && (
                  <DataTable.Title
                    key={'assignee'}
                    style={cellStyle}
                    onPress={sort('assignee')}
                    sortDirection={
                      activeSort == 'assignee' ? sortDirection : null
                    }
                  >
                    Assignee
                  </DataTable.Title>
                )}
                {showApprovalsInTable && (
                  <DataTable.Title
                    key={'approvals'}
                    style={cellStyle}
                    onPress={sort('approvals')}
                    sortDirection={
                      activeSort == 'approvals' ? sortDirection : null
                    }
                  >
                    Approvals
                  </DataTable.Title>
                )}
                {sortedColumnHeaders?.map((columnHeaderField, index) => {
                  return (
                    <DataTable.Title
                      key={index}
                      style={cellStyle}
                      onPress={sort(columnHeaderField.field_data?.property)}
                      sortDirection={
                        activeSort == columnHeaderField.field_data?.property
                          ? sortDirection
                          : null
                      }
                    >
                      {columnHeaderField.field_data.title}
                    </DataTable.Title>
                  )
                })}
              </DataTable.Header>
              <View style={{ flex: 1 }} key={'list-container'}>
                <FlatList
                  numColumns={1}
                  refreshControl={
                    <RefreshControl
                      refreshing={refreshing}
                      onRefresh={onRefresh}
                    />
                  }
                  /* Must not have flex to scroll on mobile device. */
                  contentContainerStyle={{ paddingBottom: 30, flex: 1 }}
                  onEndReached={loadMoreItems}
                  onEndReachedThreshold={400}
                  keyExtractor={(item) => item.id.toString()}
                  data={resortedEntityListItems}
                  renderItem={({ item, index: rowIndex }) => (
                    <DataTableEntityRow
                      item={item}
                      index={rowIndex}
                      reportEntity={reportEntity}
                      showLabelInTable={showLabelInTable}
                      showAssigneeInTable={showAssigneeInTable}
                      showApprovalsInTable={showApprovalsInTable}
                      sortedColumnHeaders={sortedColumnHeaders}
                      cellStyle={cellStyle}
                      canNavigate={canNavigate}
                      shouldSplit={shouldSplit}
                      splitviewNavigate={splitviewNavigate}
                      linkTo={linkTo}
                    />
                  )}
                />
              </View>
            </DataTable>
          </View>
        </ScrollView>
      )}
    </>
  )
}

export default DataTableWrapper
